import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { authAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { usePostHog } from "@posthog/react";

const RubricLogo = ({ white = false }) => {
  const color = white ? "#ffffff" : "#000000";
  return (
    <svg viewBox="30 45 465 110" height="34" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(45, 65) scale(0.68)">
        <rect x="0" y="0" width="36" height="36" rx="4" fill={color} />
        <rect
          x="48"
          y="0"
          width="36"
          height="36"
          rx="4"
          fill="none"
          stroke={color}
          strokeWidth="6"
        />
        <rect x="0" y="48" width="36" height="36" rx="4" fill={color} />
        <rect x="48" y="48" width="36" height="36" rx="4" fill={color} />
      </g>
      <text
        x="118"
        y="122"
        fill={color}
        style={{
          fontFamily: "'Source Code Pro', monospace",
          fontSize: "72px",
          fontWeight: "700",
        }}
      >
        rubricHQ
      </text>
    </svg>
  );
};

// Error codes the Google OAuth callback redirects back with, mapped to the
// copy shown above the login form. Unknown codes render nothing rather than
// echoing the raw code at the user.
const OAUTH_ERROR_MESSAGES = {
  account_archived:
    "This account has been archived. Please contact your administrator.",
  email_unverified:
    "Google hasn't verified that email address, so we can't sign you in with it.",
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, setToken, setUser } = useAuth();
  const posthog = usePostHog();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // The Google OAuth callback can't render into this page, so it bounces back
  // here with ?error=<code> (Users::OmniauthCallbacksController). Nothing was
  // reading that param, so those redirects landed on a blank login form with no
  // explanation — including the pre-existing email_unverified case.
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("error");
    if (!code) return;
    setError(OAUTH_ERROR_MESSAGES[code] || "");
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await authAPI.login(email, password);

      if (response?.status?.code === 200) {
        const { token, user: userData } = response.data;

        if (token) {
          setToken(token);

          if (userData) {
            const completeUserData = {
              ...userData,
              client_id: userData.client_id || userData.client?.id || null,
            };
            setUser(completeUserData);
            if (rememberMe) {
              localStorage.setItem(
                "userData",
                JSON.stringify(completeUserData),
              );
            }
            // Identify by email (stable distinct_id) — never by raw id/uuid.
            posthog?.identify(userData.email || String(userData.id), {
              email: userData.email,
              name: userData.name,
            });
            posthog?.capture('user_logged_in');
          }
        } else {
          setError("Authentication failed: No token received");
        }
      } else {
        setError(
          response?.status?.message ||
            "Authentication failed: Invalid response format",
        );
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Invalid email or password");
    }
  };

  const handleGoogleLogin = () => {
    const returnUrl = encodeURIComponent(`${window.location.origin}/`);
    window.location.href = `${process.env.REACT_APP_API_BASE_URL || "http://localhost:3001"}/users/auth/google_oauth2?return_to=${returnUrl}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-black flex-col justify-between p-12">
        <div className="self-start"><RubricLogo white /></div>
        <div>
          <h2 className="text-white text-4xl font-light leading-snug mb-4">
            Test AI agents at scale.
            <br />
            Ship with confidence.
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Simulate thousands of voice conversations, evaluate quality, and
            optimize your prompts — all in one platform.
          </p>
        </div>
        <p className="text-muted-foreground text-xs">
          © 2026 RubricMind Inc. All rights reserved.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 bg-card">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-foreground mb-1 text-center">
              Welcome back
            </h1>
            <p className="text-muted-foreground text-sm text-center">
              Sign in to your account to continue
            </p>
          </div>

          {/* Google button */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
          >
            <img
              className="h-5 w-5"
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google"
            />
            Continue with Google
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card px-3 text-xs text-muted-foreground">
                or continue with email
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email-address"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Email
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground"
                >
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-input text-foreground focus:ring-gray-900"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember-me" className="text-sm text-muted-foreground">
                Remember me
              </label>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full py-2.5 px-4 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              Sign in
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed">
            By signing in, you agree to our{" "}
            <a
              href="https://rubrichq.io/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:underline"
            >
              Privacy Policy
            </a>{" "}
            &amp;{" "}
            <a
              href="https://rubrichq.io/terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:underline"
            >
              Terms
            </a>
            .
          </p>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="font-medium text-foreground hover:underline"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
